import { useEffect, useRef, useState } from 'react';
import { DeliveryMonitorKpiSnapshotType } from '../../../graphql/types/deliveryMonitor/DeliveryMonitorTypes';

export interface AnimatedKpiValues {
    scheduledParcels: number;
    actualPickedUp: number;
    deliveredParcels: number;
    /** 百分数值域（0-100）；卡片渲染时保留 2 位小数 */
    pickupRate: number;
    deliveryRate: number;
}

// 每 tick 触发一次 setValues → 数字跳一格。用户预期"看到 1s 左右跳一下"，
// 所以 tick 用固定 1s 节拍；总时长由外部传入的 durationMs 决定，默认对齐 KPI poll 间隔
const TICK_MS = 1000;
const DEFAULT_DURATION_MS = 600_000;

const toNumeric = (
    snapshot: DeliveryMonitorKpiSnapshotType | null | undefined,
): AnimatedKpiValues | null => {
    if (!snapshot) return null;
    return {
        scheduledParcels: Number(snapshot.scheduledParcels) || 0,
        actualPickedUp: Number(snapshot.actualPickedUp) || 0,
        deliveredParcels: Number(snapshot.deliveredParcels) || 0,
        pickupRate: Number(snapshot.pickupRate) || 0,
        deliveryRate: Number(snapshot.deliveryRate) || 0,
    };
};

// 本地已展示值兜底：previous 来自后端上一个 slot（10min 前），
// 而用户本地可能已经看过更晚的 current。若下一轮 poll 回来的 previous
// 比"本地已展示到的值"还小，动画会呈现"数字回退"的观感。
// 用 sessionStorage 存本地最后展示到的 5 个 KPI 值，起补间时按字段取 max。
const readBaseline = (key: string | null): AnimatedKpiValues | null => {
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<AnimatedKpiValues> | null;
        if (!parsed) return null;
        return {
            scheduledParcels: Number(parsed.scheduledParcels) || 0,
            actualPickedUp: Number(parsed.actualPickedUp) || 0,
            deliveredParcels: Number(parsed.deliveredParcels) || 0,
            pickupRate: Number(parsed.pickupRate) || 0,
            deliveryRate: Number(parsed.deliveryRate) || 0,
        };
    } catch {
        return null;
    }
};

const writeBaseline = (key: string | null, values: AnimatedKpiValues): void => {
    if (!key) return;
    try {
        sessionStorage.setItem(key, JSON.stringify(values));
    } catch {
        // sessionStorage 满 / 隐私模式禁用等异常吞掉，不影响补间本身
    }
};

const maxAcrossFields = (
    a: AnimatedKpiValues,
    b: AnimatedKpiValues,
): AnimatedKpiValues => ({
    scheduledParcels: Math.max(a.scheduledParcels, b.scheduledParcels),
    actualPickedUp: Math.max(a.actualPickedUp, b.actualPickedUp),
    deliveredParcels: Math.max(a.deliveredParcels, b.deliveredParcels),
    pickupRate: Math.max(a.pickupRate, b.pickupRate),
    deliveryRate: Math.max(a.deliveryRate, b.deliveryRate),
});

/**
 * previous → current 的补间动画。
 * - current 变化且 calcSlot 前进时启动新一轮动画
 * - 起点：previous > 本地缓存兜底 > 0（首屏 count-up）
 * - 起点等于终点直接 snap，不闪烁
 * - 匀速 linear 从 start 走到 target，总时长 durationMs（默认 10 min 对齐 poll 间隔）
 * - 用 setInterval 而非 rAF：观感是"每 1s 跳一步"，符合"慢慢加"的诉求
 * - 组件卸载 / 新一轮启动时清 timer，避免叠加
 */
export const useKpiInterpolation = (
    current: DeliveryMonitorKpiSnapshotType | null | undefined,
    previous: DeliveryMonitorKpiSnapshotType | null | undefined,
    storageKey: string | null = null,
    durationMs: number = DEFAULT_DURATION_MS,
): AnimatedKpiValues | null => {
    const [values, setValues] = useState<AnimatedKpiValues | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lastSlotRef = useRef<string | null>(null);
    const lastKeyRef = useRef<string | null>(null);

    // 卸载时清 timer；不要放到主 effect 的 return 里 —— 那样 deps 每次变化都会
    // 触发 cleanup 干掉正在跑的 setInterval，而后续 effect 又因 slot 未变早返回
    // 不再 schedule 新的 interval，导致 tween 中途死掉，观感上"没有补间"。
    useEffect(() => {
        return () => {
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const clearTimer = () => {
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };

        const rawTarget = toNumeric(current);
        if (!rawTarget) return;

        // 筛选器变了（storageKey 变）→ 强制重启动画：切 HUB/WHS 后后端可能回同一个 calcSlot
        // 但值完全不同，只按 slot 判断会漏掉这次更新，displayXxx 会卡在旧筛选器的动画结果上
        const key = storageKey;
        if (key !== lastKeyRef.current) {
            lastKeyRef.current = key;
            lastSlotRef.current = null;
        }

        // calcSlot 未推进就别重启动画（同一轮 poll 回来的引用换了但数据没变的情况）
        const slot = current?.calcSlot ?? null;
        if (slot !== null && slot === lastSlotRef.current) return;

        lastSlotRef.current = slot;

        const rawStart = toNumeric(previous);
        const baseline = readBaseline(storageKey);
        const start: AnimatedKpiValues = rawStart
            ? baseline
                ? maxAcrossFields(rawStart, baseline)
                : rawStart
            : baseline ?? {
                  scheduledParcels: 0,
                  actualPickedUp: 0,
                  deliveredParcels: 0,
                  pickupRate: 0,
                  deliveryRate: 0,
              };
        // target 也做单调兜底：当日累计指标只增不减，若后端因数仓抖动回退，
        // 就把目标抬到"本地已展示到的值"，避免动画从当前帧反向补到更小的 target
        const target: AnimatedKpiValues = baseline
            ? maxAcrossFields(rawTarget, baseline)
            : rawTarget;

        if (
            start.scheduledParcels === target.scheduledParcels &&
            start.actualPickedUp === target.actualPickedUp &&
            start.deliveredParcels === target.deliveredParcels &&
            start.pickupRate === target.pickupRate &&
            start.deliveryRate === target.deliveryRate
        ) {
            clearTimer();
            setValues(target);
            writeBaseline(storageKey, target);
            return;
        }

        clearTimer();
        const startTime = performance.now();
        const step = () => {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(1, elapsed / durationMs);
            const scheduledNow =
                start.scheduledParcels + (target.scheduledParcels - start.scheduledParcels) * progress;
            const pickedUpNow =
                start.actualPickedUp + (target.actualPickedUp - start.actualPickedUp) * progress;
            const deliveredNow =
                start.deliveredParcels + (target.deliveredParcels - start.deliveredParcels) * progress;
            // 比率必须从「页面渲染的整数分子/分母」推导：卡片用 Math.round 显示整数，
            // 若用补间浮点计算，会出现"实际领取显示 100 一格没动，但率一直往上跳"的错位
            // （浮点 99.5→100.4 都渲染成 100，但率对浮点变化敏感）。用四舍五入后的整数，
            // 保证率只在显示整数真的跳格时才动一次。分母为 0 时落回后端插值值，避免除零。
            const shownScheduled = Math.round(scheduledNow);
            const shownPicked = Math.round(pickedUpNow);
            const shownDelivered = Math.round(deliveredNow);
            const pickupRateNow =
                shownScheduled > 0
                    ? (shownPicked / shownScheduled) * 100
                    : start.pickupRate + (target.pickupRate - start.pickupRate) * progress;
            const deliveryRateNow =
                shownScheduled > 0
                    ? (shownDelivered / shownScheduled) * 100
                    : start.deliveryRate + (target.deliveryRate - start.deliveryRate) * progress;
            setValues({
                scheduledParcels: scheduledNow,
                actualPickedUp: pickedUpNow,
                deliveredParcels: deliveredNow,
                pickupRate: pickupRateNow,
                deliveryRate: deliveryRateNow,
            });
            if (progress >= 1) {
                clearTimer();
                writeBaseline(storageKey, target);
            }
        };
        step();
        timerRef.current = setInterval(step, TICK_MS);
    }, [current, previous, storageKey, durationMs]);

    return values;
};
